import { useEffect, useRef, useState, useCallback } from "react";
import { Camera, X, ChevronLeft, ChevronRight } from "lucide-react";
import { galleryImages } from "../data";

export default function Gallery() {
  const sectionRef = useRef(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("fade-in-up");
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll(".animate-on-scroll");
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const openModal = (index) => {
    setCurrentIndex(index);
    setSelectedImage(galleryImages[index]);
  };

  const closeModal = () => {
    setSelectedImage(null);
  };

  // FIXED: useCallback for nextImage
  const nextImage = useCallback(() => {
    const newIndex = (currentIndex + 1) % galleryImages.length;
    setCurrentIndex(newIndex);
    setSelectedImage(galleryImages[newIndex]);
  }, [currentIndex]);

  // FIXED: useCallback for prevImage
  const prevImage = useCallback(() => {
    const newIndex = (currentIndex - 1 + galleryImages.length) % galleryImages.length;
    setCurrentIndex(newIndex);
    setSelectedImage(galleryImages[newIndex]);
  }, [currentIndex]);

  // Handle keyboard navigation - FIXED: added nextImage and prevImage to deps
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedImage) return;
      if (e.key === "Escape") closeModal();
      if (e.key === "ArrowRight") nextImage();
      if (e.key === "ArrowLeft") prevImage();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedImage, nextImage, prevImage]);

  return (
    <>
      <section ref={sectionRef} id="gallery" className="py-16 md:py-24 px-4 md:px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 animate-on-scroll opacity-0">
            <div className="inline-flex items-center px-4 py-2 bg-[#4a7c59]/10 rounded-full mb-4">
              <Camera className="w-4 h-4 text-[#4a7c59] mr-2" />
              <span className="text-[#4a7c59] text-sm font-semibold">Our Works</span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              <span className="text-gray-800">Photo </span>
              <span className="text-gradient-olive">Gallery</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-sm md:text-base">
              Take a virtual tour of our state-of-the-art facilities across all branches
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-[#4a7c59] to-[#2d5a3f] mx-auto mt-4 rounded-full"></div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {galleryImages.map((img, idx) => (
              <div
                key={img.id || idx}
                className="animate-on-scroll opacity-0 group cursor-pointer"
                style={{ transitionDelay: `${idx * 100}ms` }}
                onClick={() => openModal(idx)}
              >
                <div className="relative overflow-hidden rounded-xl md:rounded-2xl shadow-lg group aspect-square">
                  <img
                    src={img.image}
                    alt={img.alt}
                    className="w-full h-full object-cover transform transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-0 left-0 right-0 p-3 md:p-4">
                      <p className="text-white text-xs md:text-sm font-medium">{img.title}</p>
                    </div>
                  </div>
                  <div className="absolute top-2 right-2 md:top-4 md:right-4 w-8 h-8 md:w-10 md:h-10 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <Camera className="w-4 h-4 md:w-5 md:h-5 text-[#4a7c59]" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-lg" 
          onClick={closeModal}
        >
          <div className="relative max-w-5xl mx-4" onClick={(e) => e.stopPropagation()}>
            <button 
              onClick={closeModal} 
              className="absolute -top-10 md:-top-12 right-0 text-white hover:text-[#4a7c59] transition-colors" 
              aria-label="Close"
            >
              <X className="w-6 h-6 md:w-8 md:h-8" />
            </button>
            <button 
              onClick={prevImage} 
              className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 p-1.5 md:p-2 rounded-full transition-colors" 
              aria-label="Previous"
            >
              <ChevronLeft className="w-5 h-5 md:w-8 md:h-8 text-white" />
            </button>
            <button 
              onClick={nextImage} 
              className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 p-1.5 md:p-2 rounded-full transition-colors" 
              aria-label="Next"
            >
              <ChevronRight className="w-5 h-5 md:w-8 md:h-8 text-white" />
            </button>
            
            <img 
              src={selectedImage.image} 
              alt={selectedImage.alt} 
              className="max-h-[80vh] md:max-h-[90vh] w-auto rounded-xl md:rounded-2xl shadow-2xl" 
            />
            
            <div className="absolute bottom-2 md:bottom-4 left-1/2 -translate-x-1/2 bg-black/50 backdrop-blur-sm px-3 py-1 md:px-6 md:py-2 rounded-full">
              <p className="text-white text-xs md:text-sm">{selectedImage.title}</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}